import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/types/content';

interface HeroCarouselProps {
  dramas: Drama[];
}

export default function HeroCarousel({ dramas }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (dramas.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % dramas.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [dramas.length]);

  if (dramas.length === 0) return null;

  const goPrev = () => setActive((prev) => (prev - 1 + dramas.length) % dramas.length);
  const goNext = () => setActive((prev) => (prev + 1) % dramas.length);

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {dramas.map((drama, index) => (
        <div
          key={drama.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={drama.banner}
            alt={`${drama.title} 焦点剧集剧照`}
            title={`${drama.title} - 上司的老婆剧情介绍`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/85 via-primary-950/65 to-primary-950/85"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center justify-center px-4 md:px-6">
        <div className="w-full max-w-3xl text-center flex flex-col items-center animate-fade-up">
          <h1 className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/90 text-background-50 text-xs md:text-sm font-medium mb-5">
            <i className="ri-fire-fill"></i>
            上司的老婆 · 电视剧剧情介绍与角色解析平台
          </h1>
          <h2 className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight">
            {dramas[active].title}
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-4 text-sm text-background-200">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400"></i>
              {dramas[active].rating.toFixed(1)} 分
            </span>
            <span className="w-px h-4 bg-background-50/30"></span>
            <span>{dramas[active].region}</span>
            <span className="w-px h-4 bg-background-50/30"></span>
            <span>{dramas[active].year} 年</span>
            <span className="w-px h-4 bg-background-50/30"></span>
            <span>{dramas[active].status}</span>
          </div>
          <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-2xl line-clamp-3">
            {dramas[active].synopsis}
          </p>
          <div className="flex items-center gap-3 mt-7">
            <Link
              to={`/detail/${dramas[active].id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line"></i>
              查看剧情详情
            </Link>
            <Link
              to={`/category?genre=${encodeURIComponent(dramas[active].genres[0])}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-layout-grid-line"></i>
              更多同类剧集
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={goPrev}
        aria-label="上一部"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="下一部"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {dramas.map((drama, index) => (
          <button
            key={drama.id}
            type="button"
            aria-label={`切换到 ${drama.title}`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'}`}
          ></button>
        ))}
      </div>
    </section>
  );
}